import { UseFormReturn } from "react-hook-form";
import { IAdminDetailRes } from "@/services/admin-management-service";
import { AdminFormData } from "./admin-detail-schema";
import { formatDate, shortId } from "@/lib/utils";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusRadioGroup } from "./status-radio-group";

const InfoRow = ({ label, value, title }: { label: string; value: string; title?: string }) => (
  <div className="flex items-center justify-between py-3 border-b border-[#00000017] last:border-b-0">
    <span className="text-base text-[#000000] shrink-0">{label}</span>
    <span className="text-base text-[#000000]" title={title}>
      {value}
    </span>
  </div>
);

interface AdminInfoCardProps {
  detail: IAdminDetailRes | null;
  isView: boolean;
  isCreate: boolean;
  form: UseFormReturn<AdminFormData>;
}

export const AdminInfoCard = ({ detail, isView, isCreate, form }: AdminInfoCardProps) => {
  if (isView && detail) {
    return (
      <div className="bg-white rounded p-5 border border-[#00000017]">
        <h3 className="text-xl font-bold text-[#000000] mb-4">관리자 상세 정보</h3>
        <div className="flex flex-col gap-3">
          <InfoRow label="Admin ID" value={`#${shortId(detail.id)}`} />
          
          <InfoRow label="등록일" value={formatDate(detail.created_at, "YYYY-MM-DD")} />
          <InfoRow label="등록자" value={detail.created_by_name} />
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form className="bg-white rounded-[12px] p-5 flex flex-col gap-6 border border-[#00000017]">
        <h2 className="text-xl font-bold text-[#000000]">
          {isCreate ? "관리자 등록" : "관리자 수정"}
        </h2>

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>이름 <span className="text-[#D73D1D]">*</span></FormLabel>
              <FormControl>
                <Input
                  type="text"
                  placeholder="이름 입력..."
                  maxLength={50}
                  {...field}
                  onChange={(e) => field.onChange(e.target.value.slice(0, 50))}
                  className="w-full h-12 px-4   outline-none"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={`${isCreate ? `text-[#000000]` : `text-[#D8D8D8]`}`}>이메일 <span className="text-[#D73D1D]">*</span></FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="이메일 입력..."
                  maxLength={72}
                  {...field}
                  onChange={(e) => field.onChange(e.target.value.slice(0, 72))}
                  disabled={!isCreate}
                  className="w-full h-12 px-4 outline-none disabled:text-[#D8D8D8] disabled:font-bold"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel>역할 <span className="text-[#D73D1D]">*</span></FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full h-12 px-4  border border-[#00000027] text-base text-[#000000] outline-none font-bold">
                      <SelectValue placeholder="역할 선택..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="administrator">Administrator</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>상태 <span className="text-[#D73D1D]">*</span></FormLabel>
                <StatusRadioGroup value={field.value} onChange={field.onChange} />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  );
};
